import {useCallback, useState} from 'react';

import {importEntitlements} from 'src/openapi/admin/apis/EntitlementImportApi';
import {useAppDispatch} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';

import type {EntitlementTransferContext} from 'src/openapi/admin/models/EntitlementTransferContext';
import type {MigrationRelationResult} from 'src/openapi/admin/models/MigrationRelationResult';

export const useImportEntitlements = () => {
    const apiDispatch = useAppDispatch();

    const [isImporting, setIsImporting] = useState(false);
    const [importError, setImportError] = useState<string | null>(null);
    const [importResult, setImportResult] = useState<MigrationRelationResult[] | null>(null);

    const importContext = useCallback(async (context: EntitlementTransferContext) => {
        if (isImporting) {
            return;
        }

        setImportError(null);
        setIsImporting(true);
        setImportResult(null);

        try {
            const action = await apiDispatch(importEntitlements({entitlementTransferContext: context}));

            if (action.payload.success) {
                setImportResult(action.payload.data);
                return {success: true, data: action.payload.data};
            }

            const errorMessage = action.payload.data
                ? getError({error: action.payload.data})
                : 'Failed to import entitlements.';

            setImportError(errorMessage);
            return {success: false, error: errorMessage};
        } catch {
            const errorMessage = 'Failed to import entitlements.';
            setImportError(errorMessage);
            return {success: false, error: errorMessage};
        } finally {
            setIsImporting(false);
        }
    }, [apiDispatch, isImporting]);

    const resetImportState = useCallback(() => {
        setImportResult(null);
        setImportError(null);
    }, []);

    return {
        importContext,
        importResult,
        importError,
        isImporting,
        resetImportState,
    };
};
